import {
  buildingCornersLm,
  edgeOutwardNormal,
  getBuildingHeight,
  polygonCentroid,
} from './geometry';
import type { Building, Point, State, Zoning, ZoningDistrictType } from './types';

// 建築基準法の高さ制限の考え方: 敷地境界線から離れるほど建てられる高さが増える(勾配=slopeRatio)。
// 道路斜線は道路の反対側の境界線、隣地斜線・北側斜線は自地の隣地境界線・真北方向の境界線を基準とし、
// 隣地斜線・北側斜線は基準線からriseM分だけ無条件に立ち上げてから勾配が始まる。
// 建物の各サンプル点(4隅+各辺中点)で計算し、最も厳しい(低い)許容高さをその建物の判定値とする。

type ZoningPreset = Pick<Zoning, 'road' | 'adjacent' | 'northSlope'> & { label: string };

export const ZONING_DISTRICT_PRESETS: Record<ZoningDistrictType, ZoningPreset> = {
  none: {
    label: '指定なし',
    road: { slopeRatio: 1.25, applyDistanceM: 20 },
    adjacent: { enabled: true, riseM: 20, slopeRatio: 1.25 },
    northSlope: { enabled: false, riseM: 5, slopeRatio: 1.25 },
  },
  cat1low: {
    label: '第一種低層住居専用地域',
    road: { slopeRatio: 1.25, applyDistanceM: 20 },
    adjacent: { enabled: false, riseM: 20, slopeRatio: 1.25 },
    northSlope: { enabled: true, riseM: 5, slopeRatio: 1.25 },
  },
  cat2low: {
    label: '第二種低層住居専用地域',
    road: { slopeRatio: 1.25, applyDistanceM: 20 },
    adjacent: { enabled: false, riseM: 20, slopeRatio: 1.25 },
    northSlope: { enabled: true, riseM: 5, slopeRatio: 1.25 },
  },
  denen: {
    label: '田園住居地域',
    road: { slopeRatio: 1.25, applyDistanceM: 20 },
    adjacent: { enabled: false, riseM: 20, slopeRatio: 1.25 },
    northSlope: { enabled: true, riseM: 5, slopeRatio: 1.25 },
  },
  cat1mid: {
    label: '第一種中高層住居専用地域',
    road: { slopeRatio: 1.25, applyDistanceM: 20 },
    adjacent: { enabled: true, riseM: 20, slopeRatio: 1.25 },
    northSlope: { enabled: true, riseM: 10, slopeRatio: 1.25 },
  },
  cat2mid: {
    label: '第二種中高層住居専用地域',
    road: { slopeRatio: 1.25, applyDistanceM: 20 },
    adjacent: { enabled: true, riseM: 20, slopeRatio: 1.25 },
    northSlope: { enabled: true, riseM: 10, slopeRatio: 1.25 },
  },
  cat1res: {
    label: '第一種住居地域',
    road: { slopeRatio: 1.25, applyDistanceM: 20 },
    adjacent: { enabled: true, riseM: 20, slopeRatio: 1.25 },
    northSlope: { enabled: false, riseM: 5, slopeRatio: 1.25 },
  },
  cat2res: {
    label: '第二種住居地域',
    road: { slopeRatio: 1.25, applyDistanceM: 20 },
    adjacent: { enabled: true, riseM: 20, slopeRatio: 1.25 },
    northSlope: { enabled: false, riseM: 5, slopeRatio: 1.25 },
  },
  quasiRes: {
    label: '準住居地域',
    road: { slopeRatio: 1.25, applyDistanceM: 20 },
    adjacent: { enabled: true, riseM: 20, slopeRatio: 1.25 },
    northSlope: { enabled: false, riseM: 5, slopeRatio: 1.25 },
  },
  neighborCom: {
    label: '近隣商業地域',
    road: { slopeRatio: 1.5, applyDistanceM: 25 },
    adjacent: { enabled: true, riseM: 31, slopeRatio: 2.5 },
    northSlope: { enabled: false, riseM: 5, slopeRatio: 1.25 },
  },
  commercial: {
    label: '商業地域',
    road: { slopeRatio: 1.5, applyDistanceM: 25 },
    adjacent: { enabled: true, riseM: 31, slopeRatio: 2.5 },
    northSlope: { enabled: false, riseM: 5, slopeRatio: 1.25 },
  },
  quasiIndus: {
    label: '準工業地域',
    road: { slopeRatio: 1.5, applyDistanceM: 25 },
    adjacent: { enabled: true, riseM: 31, slopeRatio: 2.5 },
    northSlope: { enabled: false, riseM: 5, slopeRatio: 1.25 },
  },
  indus: {
    label: '工業地域',
    road: { slopeRatio: 1.5, applyDistanceM: 25 },
    adjacent: { enabled: true, riseM: 31, slopeRatio: 2.5 },
    northSlope: { enabled: false, riseM: 5, slopeRatio: 1.25 },
  },
  indusEx: {
    label: '工業専用地域',
    road: { slopeRatio: 1.5, applyDistanceM: 25 },
    adjacent: { enabled: true, riseM: 31, slopeRatio: 2.5 },
    northSlope: { enabled: false, riseM: 5, slopeRatio: 1.25 },
  },
};

export function applyZoningPreset(zoning: Zoning, districtType: ZoningDistrictType): void {
  const p = ZONING_DISTRICT_PRESETS[districtType];
  if (!p) return;
  zoning.districtType = districtType;
  zoning.road = { ...p.road };
  zoning.adjacent = { ...p.adjacent };
  zoning.northSlope = { ...p.northSlope };
}

export function distanceToEdgeAlongNorth(pt: Point, p1: Point, p2: Point): number | null {
  if (p1.x === p2.x) return null;
  const t = (pt.x - p1.x) / (p2.x - p1.x);
  if (t < 0 || t > 1) return null;
  const iy = p1.y + t * (p2.y - p1.y);
  if (iy <= pt.y) return null;
  return iy - pt.y;
}

function getSlopeSamplePoints(b: Building): Point[] {
  const c = buildingCornersLm(b);
  const mid = (p: Point, q: Point): Point => ({ x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 });
  return [
    c[0],
    c[1],
    c[2],
    c[3],
    mid(c[0], c[1]),
    mid(c[1], c[2]),
    mid(c[2], c[3]),
    mid(c[3], c[0]),
  ];
}

export interface AllowedHeights {
  road: number;
  adjacent: number;
  north: number;
}

export function computeAllowedHeightsAtPoint(pt: Point, state: State): AllowedHeights {
  const pts = state.site.points;
  const edges = state.site.edges;
  const centroid = pts.length >= 3 ? polygonCentroid(pts) : { x: 0, y: 0 };
  let road = Infinity;
  let adjacent = Infinity;
  let north = Infinity;
  for (let i = 0; i < pts.length; i++) {
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    const edge = edges[i] || { type: 'none' as const, roadWidthM: null, driveways: [] };
    const n = edgeOutwardNormal(p1, p2, centroid);
    if (edge.type === 'road' && edge.roadWidthM && edge.roadWidthM > 0) {
      // 道路斜線の起点は敷地側の境界線ではなく、道路を挟んだ反対側の境界線
      const basePt = { x: p1.x + n.x * edge.roadWidthM, y: p1.y + n.y * edge.roadWidthM };
      const d = (basePt.x - pt.x) * n.x + (basePt.y - pt.y) * n.y;
      if (d >= 0 && d <= state.zoning.road.applyDistanceM) {
        const allowed = d * state.zoning.road.slopeRatio;
        if (allowed < road) road = allowed;
      }
    }
    if (edge.type === 'neighbor') {
      const d = (p1.x - pt.x) * n.x + (p1.y - pt.y) * n.y;
      if (state.zoning.adjacent.enabled && d >= 0) {
        const allowed = state.zoning.adjacent.riseM + d * state.zoning.adjacent.slopeRatio;
        if (allowed < adjacent) adjacent = allowed;
      }
      if (state.zoning.northSlope.enabled) {
        const nd = distanceToEdgeAlongNorth(pt, p1, p2);
        if (nd !== null) {
          const allowed = state.zoning.northSlope.riseM + nd * state.zoning.northSlope.slopeRatio;
          if (allowed < north) north = allowed;
        }
      }
    }
  }
  return { road, adjacent, north };
}

export interface SlopeCheckLimit {
  allowed: number;
  applicable: boolean;
  ok: boolean;
  overM: number;
}

export interface SlopeCheckResult {
  buildingId: string;
  heightM: number;
  road: SlopeCheckLimit;
  adjacent: SlopeCheckLimit;
  north: SlopeCheckLimit;
  overallOk: boolean;
}

export function computeSlopeCheck(state: State): Map<string, SlopeCheckResult> {
  const result = new Map<string, SlopeCheckResult>();
  if (state.site.points.length < 3) return result;
  state.buildings.forEach((b: Building) => {
    const heightM = getBuildingHeight(b);
    const pts = getSlopeSamplePoints(b);
    const worst = { road: Infinity, adjacent: Infinity, north: Infinity };
    pts.forEach((pt) => {
      const a = computeAllowedHeightsAtPoint(pt, state);
      if (a.road < worst.road) worst.road = a.road;
      if (a.adjacent < worst.adjacent) worst.adjacent = a.adjacent;
      if (a.north < worst.north) worst.north = a.north;
    });
    const mk = (allowed: number): SlopeCheckLimit => ({
      allowed,
      applicable: allowed < Infinity,
      ok: allowed >= Infinity || heightM <= allowed,
      overM: allowed < Infinity ? Math.max(0, heightM - allowed) : 0,
    });
    const road = mk(worst.road);
    const adjacent = mk(worst.adjacent);
    const north = mk(worst.north);
    result.set(b.id, {
      buildingId: b.id,
      heightM,
      road,
      adjacent,
      north,
      overallOk: road.ok && adjacent.ok && north.ok,
    });
  });
  return result;
}
